import React, { useEffect, useState, useCallback } from 'react';
import { getAuthAxios } from '@/store';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  FileText,
  Building2,
  Save,
  RotateCcw,
  Loader2,
  Info,
  MessageCircle,
  Clock,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/components/I18nProvider';

const TEMPLATE_META = {
  payment_created: {
    name: 'Ödeme Sözü Alındı',
    desc: 'Yeni ödeme sözü oluşturulduğunda müşteriye gönderilen mesaj',
    icon: MessageCircle,
    color: 'text-green-600 bg-green-100',
  },
  reminder: {
    name: 'Vade Hatırlatması',
    desc: 'Vade günü gelen ödemeler için müşteriye gönderilen hatırlatma',
    icon: Clock,
    color: 'text-amber-600 bg-amber-100',
  },
};

const VARIABLES_INFO = [
  { key: '{musteri_adi}', desc: 'Müşteri/cari adı' },
  { key: '{tutar}', desc: 'Ödeme tutarı' },
  { key: '{vade_tarihi}', desc: 'Vade tarihi' },
  { key: '{firma_adi}', desc: 'Firma adı' },
];

export default function AdminTemplatesPage() {
  const { t } = useI18n();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [editedContent, setEditedContent] = useState({});

  const fetchCompanies = useCallback(async () => {
    try {
      const api = getAuthAxios();
      const res = await api.get('/admin/companies');
      setCompanies(res.data);
      if (res.data.length > 0 && !selectedCompany) {
        setSelectedCompany(res.data[0].id);
      }
    } catch (err) {
      toast.error('Firmalar yüklenemedi');
    }
  }, []);

  const fetchTemplates = useCallback(async (companyId) => {
    if (!companyId) return;
    setLoading(true);
    try {
      const api = getAuthAxios();
      const res = await api.get(`/admin/templates/${companyId}`);
      setTemplates(res.data);
      const contentMap = {};
      res.data.forEach((t) => {
        contentMap[t.template_type] = t.content;
      });
      setEditedContent(contentMap);
    } catch (err) {
      toast.error('Şablonlar yüklenemedi');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  useEffect(() => { if (selectedCompany) fetchTemplates(selectedCompany); }, [selectedCompany, fetchTemplates]);

  const handleSave = async (templateType) => {
    setSaving((s) => ({ ...s, [templateType]: true }));
    try {
      const api = getAuthAxios();
      await api.put(`/admin/templates/${selectedCompany}/${templateType}`, {
        content: editedContent[templateType],
      });
      toast.success(t('templateSaved'));
      fetchTemplates(selectedCompany);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kaydetme hatası');
    }
    setSaving((s) => ({ ...s, [templateType]: false }));
  };

  const handleReset = async (templateType) => {
    setSaving((s) => ({ ...s, [templateType]: true }));
    try {
      const api = getAuthAxios();
      await api.delete(`/admin/templates/${selectedCompany}/${templateType}`);
      toast.success('Şablon varsayılana sıfırlandı');
      fetchTemplates(selectedCompany);
    } catch (err) {
      toast.error('Sıfırlama hatası');
    }
    setSaving((s) => ({ ...s, [templateType]: false }));
  };

  const filteredCompanies = companies.filter((c) =>
    c.name.toLowerCase().includes(companySearch.toLowerCase())
  );
  const selectedCompanyName = companies.find((c) => c.id === selectedCompany)?.name || '';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-templates-page">
      <div>
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">Mesaj Şablonları</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Firma bazlı otomatik mesaj şablonlarını düzenleyin
        </p>
      </div>

      {/* Company Selector with Search */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary shrink-0" />
            <Label className="font-medium whitespace-nowrap">Firma Seçin:</Label>
            {selectedCompanyName && (
              <Badge variant="outline" className="ml-auto">{selectedCompanyName}</Badge>
            )}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Firma ara..."
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              className="pl-9"
              data-testid="template-company-search"
            />
          </div>
          <div className="max-h-40 overflow-y-auto rounded-lg border">
            {filteredCompanies.length === 0 ? (
              <div className="p-3 text-center text-sm text-muted-foreground">Firma bulunamadı</div>
            ) : (
              filteredCompanies.map((c) => (
                <button
                  key={c.id}
                  onClick={() => { setSelectedCompany(c.id); setCompanySearch(''); }}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-muted ${
                    selectedCompany === c.id ? 'bg-primary/10 font-medium text-primary' : ''
                  }`}
                  data-testid={`template-company-${c.id}`}
                >
                  <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{c.name}</span>
                </button>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Variables Info */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-sm">Kullanılabilir Değişkenler</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {VARIABLES_INFO.map((v) => (
                  <Badge key={v.key} variant="outline" className="gap-1 font-mono text-xs">
                    {v.key} <span className="font-sans text-muted-foreground">- {v.desc}</span>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => <Skeleton key={i} className="h-48 w-full rounded-xl" />)}
        </div>
      ) : (
        <div className="space-y-4">
          {templates.map((tmpl) => {
            const meta = TEMPLATE_META[tmpl.template_type] || {};
            const IconComp = meta.icon || FileText;
            const isEdited = editedContent[tmpl.template_type] !== tmpl.content;

            return (
              <Card key={tmpl.template_type} data-testid={`template-card-${tmpl.template_type}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <div className={`rounded-lg p-2 ${meta.color}`}>
                        <IconComp className="h-4 w-4" />
                      </div>
                      {meta.name || tmpl.name}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {tmpl.is_default ? (
                        <Badge variant="secondary" className="text-xs">Varsayılan</Badge>
                      ) : (
                        <Badge className="bg-primary/10 text-primary text-xs">Özelleştirilmiş</Badge>
                      )}
                    </div>
                  </div>
                  <CardDescription>{meta.desc}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Mesaj İçeriği</Label>
                    <Textarea
                      value={editedContent[tmpl.template_type] || ''}
                      onChange={(e) => setEditedContent({ ...editedContent, [tmpl.template_type]: e.target.value })}
                      rows={6}
                      className="font-mono text-sm"
                      data-testid={`template-content-${tmpl.template_type}`}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => handleSave(tmpl.template_type)}
                      disabled={saving[tmpl.template_type] || !isEdited}
                      data-testid={`save-template-${tmpl.template_type}`}
                    >
                      {saving[tmpl.template_type] ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      Kaydet
                    </Button>
                    {!tmpl.is_default && (
                      <Button
                        variant="outline"
                        onClick={() => handleReset(tmpl.template_type)}
                        disabled={saving[tmpl.template_type]}
                        data-testid={`reset-template-${tmpl.template_type}`}
                      >
                        <RotateCcw className="mr-2 h-4 w-4" />
                        Varsayılana Dön
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
